import {
  useEffect,
  useId,
  useRef,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { cn } from './util';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** Emoji / icon in the title bar. */
  icon?: ReactNode;
  children: ReactNode;
  /** Action row (buttons) rendered at the bottom. */
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** Close on backdrop click (default true). */
  dismissable?: boolean;
  /** Element to focus first (otherwise the first focusable inside the body). */
  initialFocus?: RefObject<HTMLElement | null>;
  className?: string;
}

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

function focusables(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute('data-focus-skip'),
  );
}

const WIDTH = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-3xl' } as const;

/**
 * Comic modal: yellow title strip, ink frame, tilted pop-in. Traps focus (Tab / Shift+Tab cycle),
 * closes on Esc, restores focus to the opener on close, locks page scroll while open.
 */
export function Dialog({
  open,
  onClose,
  title,
  icon,
  children,
  footer,
  size = 'md',
  dismissable = true,
  initialFocus,
  className,
}: DialogProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const raf = requestAnimationFrame(() => {
      const body = panelRef.current?.querySelector<HTMLElement>('[data-dialog-body]') ?? null;
      const target =
        initialFocus?.current ??
        focusables(body)[0] ??
        focusables(panelRef.current)[0] ??
        panelRef.current;
      target?.focus();
    });
    return () => {
      cancelAnimationFrame(raf);
      document.body.style.overflow = prevOverflow;
      opener?.focus?.();
    };
  }, [open, initialFocus]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onCloseRef.current();
      return;
    }
    if (e.key !== 'Tab') return;
    const items = focusables(panelRef.current);
    if (items.length === 0) {
      e.preventDefault();
      return;
    }
    const first = items[0]!;
    const last = items[items.length - 1]!;
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === panelRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          key="dialog"
          className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onKeyDown={onKeyDown}
        >
          <div
            aria-hidden
            className="halftone-coarse absolute inset-0 bg-ink/55"
            onClick={() => dismissable && onCloseRef.current()}
          />
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
            initial={{ scale: 0.7, rotate: -4, y: 30 }}
            animate={{ scale: 1, rotate: -0.6, y: 0 }}
            // Quick, non-spring exit so the fixed overlay is gone ~200 ms after Esc/close.
            exit={{
              scale: 0.85,
              rotate: 2,
              y: 20,
              opacity: 0,
              transition: { duration: 0.16, ease: 'easeIn' },
            }}
            transition={{ type: 'spring', stiffness: 420, damping: 24 }}
            className={cn(
              'relative flex max-h-[min(88dvh,820px)] w-full flex-col border-4 border-ink bg-white shadow-ink-xl outline-none',
              WIDTH[size],
              className,
            )}
          >
            <div className="flex items-center justify-between gap-3 border-b-4 border-ink bg-yellow px-4 py-2">
              <h2
                id={titleId}
                className="comic flex min-w-0 items-center gap-2 text-[24px] sm:text-[28px]"
              >
                {icon}
                <span className="pt-[0.06em]">{title}</span>
              </h2>
              <button
                type="button"
                aria-label="Kapat"
                onClick={() => onCloseRef.current()}
                className="comic flex size-10 shrink-0 items-center justify-center border-3 border-ink bg-white text-[22px] shadow-ink-xs transition-transform hover:rotate-6 active:translate-y-0.5 active:shadow-none sm:size-9"
              >
                ✕
              </button>
            </div>
            <div data-dialog-body className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5">
              {children}
            </div>
            {footer && (
              <div className="flex flex-wrap items-center justify-end gap-3 border-t-3 border-dashed border-ink/40 bg-paper px-4 py-3 sm:px-5">
                {footer}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
